/* eslint-disable jsx-a11y/no-autofocus */
/* This example requires Tailwind CSS v2.0+ */
import { useMemo, useRef, useState } from 'react'
import { Listbox } from '@headlessui/react'
import { CheckIcon, SelectorIcon } from '@heroicons/react/solid'
import classNames from 'classnames'
import { includes, find, debounce, isFunction, trim, toLower } from 'lodash'
import { CheckCircleIcon } from '@heroicons/react/outline'
import EmptyState from 'components/pageComponents/EmptyState'
import { useTranslation } from 'react-i18next'
import ReactDOM from 'react-dom'

export default function Selection({
  searchPlaceholder,
  showSearch,
  onSearch,
  disabled,
  height = '200px',
  value,
  error,
  onChange,
  onAllSelect,
  multiple,
  onClose,
  list = [],
  title,
  className,
  valueByName,
  dropdownPlacement = 'bottom',
  usePortalOptions = false,
}) {
  const debounceFn = onSearch ? debounce(onSearch, 500) : null
  const [search, setValue] = useState('')
  const triggerRef = useRef(null)
  const { t } = useTranslation()

  const effectiveSearchPlaceholder = searchPlaceholder || t('common.search')

  const handleChange = event => {
    setValue(event.target.value)
    if (debounceFn) {
      debounceFn(event.target.value)
    }
  }

  // Local filter when showSearch is used without a remote onSearch handler
  const filteredList = useMemo(() => {
    if (!showSearch || onSearch || !trim(search)) {
      return list
    }
    const query = toLower(trim(search))
    return list.filter(item => {
      const name = toLower(String(item?.name || ''))
      const description = toLower(String(item?.description || ''))
      return includes(name, query) || includes(description, query)
    })
  }, [list, search, showSearch, onSearch])

  return multiple ? (
    <Listbox value={value} onChange={onChange} multiple disabled={disabled}>
      {({ open }) => (
        <>
          <div className={classNames(className, !className && 'max-w-lg', 'relative', open && 'z-[9998]', error && 'error-selection')}>
            <Listbox.Button className="bg-white flex-wrap min-h-[36px] items-center flex relative w-full border border-gray-300 rounded-md pl-3 pr-10 py-0.5 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-dark-500 focus:border-dark-500 sm:text-sm">
              {value?.length > 0 ? (
                value
                  ?.map(v =>
                    // eslint-disable-next-line
                    find(list, function (item) {
                      return item.id === v
                    })
                  )
                  ?.map(val => (
                    <span key={val?.id} className="block bg-gray-200 text-sm px-2 py-0 rounded mr-1 my-1">
                      {val?.name}
                    </span>
                  ))
              ) : (
                <span className="block ml-2 text-sm truncate">{<span className="block truncate">{title}</span>}</span>
              )}
              <span className="absolute inset-y-0 right-0 flex items-center pr-2">
                {onAllSelect && isFunction(onAllSelect) && (
                  <CheckCircleIcon
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      onAllSelect(value?.length === list?.length ? [] : list.map(d => d.id))
                    }}
                    className={classNames('h-5 w-5 cursor-pointer text-gray-400', value?.length === list?.length && 'text-indigo-500')}
                    aria-hidden="true"
                  />
                )}
                <SelectorIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </span>
            </Listbox.Button>

            {/* No fade-out Transition here: a stuck leave transition left the panel invisible but still
                clickable over the fields below. Options now unmount as soon as the listbox closes. */}
            {open && (
              <Listbox.Options
                style={{ maxHeight: height }}
                className={classNames(
                  'absolute z-[9999] w-full bg-white shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm',
                  dropdownPlacement === 'top' ? 'bottom-full mb-1' : 'mt-1'
                )}
              >
                {showSearch ? (
                  <div className="p-2 -mt-1">
                    <input autoFocus className="w-full relative" placeholder={effectiveSearchPlaceholder} value={search} onChange={handleChange} />
                  </div>
                ) : null}

                {filteredList?.length > 0 ? (
                  filteredList.map(item => (
                    <Listbox.Option
                      disabled={item.disabled}
                      key={item.id}
                      className={({ active }) => classNames(active ? 'text-white bg-dark-600' : 'text-gray-700', 'cursor-default select-none relative py-2 px-3')}
                      value={item.id}
                    >
                      {({ active }) => {
                        const selected = includes(value, item.id)
                        return (
                          <>
                            <span className={classNames(item.disabled ? 'opacity-50' : '', selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                              {item?.name} <span className="pl-1 font-normal text-gray-400">{item?.description}</span>
                            </span>

                            {selected ? (
                              <span className={classNames(active ? 'text-white' : 'text-dark-600', 'absolute inset-y-0 right-0 flex items-center pr-4')}>
                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )
                      }}
                    </Listbox.Option>
                  ))
                ) : (
                  <EmptyState v2 description="No items in the list" />
                )}
              </Listbox.Options>
            )}
          </div>
        </>
      )}
    </Listbox>
  ) : (
    <Listbox
      value={valueByName ? find(list, { name: value })?.id ?? value : value}
      onChange={selectedId => {
        const item = find(list, { id: selectedId })
        if (item) onChange(item)
      }}
      disabled={disabled}
    >
      {({ open }) => (
        <>
          <div ref={triggerRef} className={classNames(className, !className && 'max-w-lg', 'relative', open && 'z-[9998]', error && 'error-selection')}>
            <Listbox.Button className="bg-white h-[37px]  w-full border border-gray-300 rounded-md pl-3 pr-10 py-1 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-dark-500 focus:border-dark-500 sm:text-sm">
              <span className="block truncate text-sm">{find(list, valueByName ? { name: value } : { id: value })?.name || title}</span>
              <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <SelectorIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </span>
            </Listbox.Button>

            {(() => {
              if (!open) return null
              const triggerRect = usePortalOptions && triggerRef.current ? triggerRef.current.getBoundingClientRect() : null
              const shouldUsePortal = usePortalOptions && triggerRect && typeof document !== 'undefined'
              const optionsNode = (
                <Listbox.Options
                  style={
                    shouldUsePortal
                      ? {
                          maxHeight: height,
                          position: 'fixed',
                          left: triggerRect.left,
                          width: triggerRect.width,
                          top: dropdownPlacement === 'top' ? triggerRect.top - 4 : triggerRect.bottom + 4,
                          transform: dropdownPlacement === 'top' ? 'translateY(-100%)' : undefined,
                        }
                      : { maxHeight: height }
                  }
                  className={classNames(
                    'z-[9999] w-full bg-white max-h-[200px] shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm',
                    shouldUsePortal ? 'fixed' : 'absolute',
                    !shouldUsePortal && (dropdownPlacement === 'top' ? 'bottom-full mb-1' : 'mt-1')
                  )}
                >
                  {showSearch ? (
                    <div className="p-2 -mt-1">
                      <input className="w-full" placeholder={effectiveSearchPlaceholder} value={search} onChange={handleChange} />
                    </div>
                  ) : null}
                  {filteredList?.length > 0 ? (
                    filteredList.map(item => (
                      <Listbox.Option
                        key={item.id}
                        className={({ active }) => classNames(active ? 'text-white bg-dark-600' : 'text-gray-900', 'cursor-default select-none relative py-2 px-3')}
                        value={item.id}
                      >
                        {({ active }) => {
                          const selectedId = valueByName ? find(list, { name: value })?.id : value
                          const selected = selectedId === item.id
                          return (
                            <>
                              <span className={classNames(selected ? 'font-medium' : 'font-normal', 'block truncate text-sm')}>{item?.name}</span>
                              {selected ? (
                                <span className={classNames(active ? 'text-white' : 'text-dark-600', 'absolute inset-y-0 right-0 flex items-center pr-4')}>
                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )
                        }}
                      </Listbox.Option>
                    ))
                  ) : (
                    <EmptyState v2 description="No items in the list" />
                  )}
                </Listbox.Options>
              )

              if (shouldUsePortal) {
                return ReactDOM.createPortal(optionsNode, document.body)
              }
              return optionsNode
            })()}
          </div>
        </>
      )}
    </Listbox>
  )
}
